/* eslint-disable no-console */

/**
 * CalmLogger brought over from https://github.com/calm/web/tree/master/libraries/logger
 */

import { datadogLogs } from '@datadog/browser-logs';
import pick from 'lodash/pick';
import createLogger, { Logger } from 'pino';
import { sanitizingStringifyReplacer } from './sanitizer';
import { JSONObject, JSONValue } from './types';

export { initDatadogLogs } from '../datadog/logger';

export interface logData {
    [key: string]: JSONValue | undefined;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const isJsonObject = (value: unknown): value is JSONObject =>
    isRecord(value) && !Array.isArray(value);

export const getSanitizedLogData = (data?: logData): logData => {
    if (!data) {
        return {};
    }
    const sanitized: unknown = JSON.parse(
        JSON.stringify(data, sanitizingStringifyReplacer())
    );
    return isJsonObject(sanitized) ? sanitized : {};
};

function hasWindowAndDocument() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}

const networkShape = {
    status: true,
    statusText: true,
    headers: true,
    config: ['method', 'url', 'params', 'headers', 'data'],
    request: ['res.headers'],
    data: true,
} as const;
const networkKeys = Object.keys(networkShape);

// TODO: how might we sanitize errors without destroying the value we're passed in?
// Currently this function destructively modifies the value it is passed
export const getSanitizedError = <T>(error: T): T => {
    if (!error || !isRecord(error)) {
        return error;
    }
    // `isRecord` lets us read and mutate the error's dynamic keys with real
    // `unknown` types; the generic return preserves the caller's type (e.g.
    // `Error` in, `Error` out).
    const errorRecord: Record<string, unknown> = error;
    const errorKeys = Object.keys(errorRecord);
    const isNetworkError =
        errorKeys.includes('status') && errorKeys.includes('statusText');
    errorKeys.forEach((key) => {
        if (['type', 'message', 'stack', 'code'].includes(key)) {
            return;
        }
        try {
            let value: unknown = errorRecord[key];
            // Network errors have a lot of nested objects, so we only want to keep the most important ones
            if (isNetworkError) {
                if (networkKeys.includes(key)) {
                    const keysToPick =
                        networkShape[key as keyof typeof networkShape];
                    if (
                        Array.isArray(keysToPick) &&
                        value &&
                        typeof value === 'object'
                    ) {
                        value = pick(value, keysToPick);
                    }
                    if (
                        key === 'config' &&
                        value &&
                        typeof value === 'object' &&
                        'data' in value &&
                        typeof value.data === 'string'
                    ) {
                        try {
                            value.data = JSON.parse(value.data);
                        } catch {
                            // ignore parsing errors for payload data
                        }
                    }
                } else {
                    delete errorRecord[key];
                    value = undefined;
                }
            }
            if (value) {
                errorRecord[key] = JSON.parse(
                    JSON.stringify(value, sanitizingStringifyReplacer())
                );
            }
        } catch {
            // ignore any errors when mucking about with formatting
        }
    });
    return error;
};

const formatForBrowser =
    (logFn: typeof console.log): ((o: object) => void) =>
    (o: object): void => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { level, time, err, msg, ...data } = o as {
            level: string | number;
            time: number;
            err?: Error;
            msg: string;
        };
        logFn(...[msg, data, err].filter(Boolean));
    };

export class CalmLogger {
    private logger: Logger;
    private shouldSkipConsoleLog: boolean;

    public constructor(
        childOptions: Parameters<
            ReturnType<typeof createLogger>['child']
        >[0] = {},
        shouldSkipConsoleLog?: boolean
    ) {
        const pino = createLogger({
            // pino represents log levels as numbers, while
            // Datadog expects them as strings. This formatter
            // ensures that the log level is sent as a string.
            formatters: {
                level(level) {
                    return { level };
                },
            },
            browser: {
                write: {
                    fatal: formatForBrowser(console.error),
                    error: formatForBrowser(console.error),
                    warn: formatForBrowser(console.warn),
                    info: formatForBrowser(console.info),
                    debug: formatForBrowser(console.debug),
                    trace: formatForBrowser(console.debug),
                },
            },
            errorKey: 'error',
        });
        this.logger = pino.child(childOptions);
        this.shouldSkipConsoleLog = shouldSkipConsoleLog ?? false;
    }

    public info(msg: string, data?: logData): void {
        const cleanData = getSanitizedLogData(data);
        if (!this.shouldSkipConsoleLog || !hasWindowAndDocument()) {
            this.logger.info(cleanData, msg);
        }
        if (hasWindowAndDocument()) {
            datadogLogs.logger.info(msg, cleanData);
        }
    }

    public warn(msg: string, data?: logData): void {
        const cleanData = getSanitizedLogData(data);
        if (!this.shouldSkipConsoleLog || !hasWindowAndDocument()) {
            this.logger.warn(cleanData, msg);
        }
        if (hasWindowAndDocument()) {
            datadogLogs.logger.warn(msg, cleanData);
        }
    }

    public debug(msg: string, data?: logData): void {
        const cleanData = getSanitizedLogData(data);
        if (!this.shouldSkipConsoleLog || !hasWindowAndDocument()) {
            this.logger.debug(cleanData, msg);
        }
        if (hasWindowAndDocument()) {
            datadogLogs.logger.debug(msg, cleanData);
        }
    }

    public fatal(msg: string, data?: logData): void {
        const cleanData = getSanitizedLogData(data);
        this.logger.fatal(cleanData, msg);
    }

    public error(msg: string, data: logData, err: unknown): void {
        const cleanData = getSanitizedLogData(data);
        // Sanitize the raw value in place (mirrors the original behavior); this
        // preserves the field-level sanitization for non-Error throwables that a
        // `new Error(String(err))` wrap would discard.
        const cleanError = getSanitizedError(err);
        if (!this.shouldSkipConsoleLog || !hasWindowAndDocument()) {
            this.logger.error({ ...cleanData, error: cleanError }, msg);
        }
        if (hasWindowAndDocument()) {
            // Datadog's logger types the error argument as `Error`; every real
            // caller passes an Error/AxiosError instance, so this narrows to the
            // same value at runtime without widening Datadog's contract.
            datadogLogs.logger.error(
                msg,
                cleanData,
                cleanError instanceof Error ? cleanError : undefined
            );
        }
    }
}

export const logger = new CalmLogger(
    process.env.NODE_ENV === 'production'
        ? {
              service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
          }
        : {}
);
